import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { Attrs, Node } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { isNodeSelection } from '@atlaskit/editor-prosemirror/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ReplaceMauiAppProps } from '../malleableUiPluginType';

/*
MAUI App ADF Node Example:
{
	"type": "extension",
	"attrs": {
		"extensionKey": "native-embed:maui",
		"extensionType": "com.atlassian.confluence.macro.core",
		"parameters": {
			"macroParams": {
				"url": {
					"value": "https://pug.jira-dev.com/remix/ui/01KKJ7GXSP13TXXTV7D98WM9QS"
				}
			},
			"localId": "92100942-81d5-42d2-a4c5-4edfdbe27de7"
		}
	}
}
*/

export const isMauiAppNode = (node: Node, previousMauiId: string, localId?: string): boolean => {
	return (
		node.type.name === 'extension' &&
		node.attrs.extensionKey === 'native-embed:maui' &&
		node.attrs.parameters?.macroParams?.url?.value.endsWith(previousMauiId) &&
		(localId ? node.attrs?.localId === localId : true)
	);
};

export const getUpdateAttrs = (attrs: Attrs, previousMauiId: string, newMauiId: string): any => {
	/**
	 * Some older browsers like chrome 97 doesn't support structuredClone.
	 * So using JSON.parse(JSON.stringify()) as a fallback.
	 */
	const newAttrs =
		typeof window !== 'undefined' && typeof window.structuredClone === 'function'
			? structuredClone(attrs)
			: JSON.parse(JSON.stringify(attrs));

	newAttrs.parameters.macroParams.url.value = newAttrs.parameters.macroParams.url.value.replace(
		previousMauiId,
		newMauiId,
	);
	return newAttrs;
};

export const replaceMauiApp =
	({
		previousMauiId,
		newMauiId,
		localId,
		suppressScrollIntoView,
	}: ReplaceMauiAppProps): EditorCommand =>
	({ tr }) => {
		// Check current selection, if the node is already selected we can skip expensive document traversal
		if (
			isNodeSelection(tr.selection) &&
			isMauiAppNode(tr.selection.node, previousMauiId, localId)
		) {
			const node = tr.selection.node;
			const pos = tr.selection.$anchor.pos;
			tr.setNodeMarkup(pos, undefined, getUpdateAttrs(node.attrs, previousMauiId, newMauiId));
			if (fg('platform_remix_fix_node_replacement_selection')) {
				tr.setSelection(NodeSelection.create(tr.doc, pos));
			}
			if (suppressScrollIntoView) {
				tr.setMeta('scrollIntoView', false);
			}
			return tr;
		}

		// Find the first node that matches the previous MAUI App ID
		let nodeResult: { node: Node; pos: number } | undefined;
		tr.doc.descendants((node, pos) => {
			if (nodeResult) {
				return false;
			}
			if (isMauiAppNode(node, previousMauiId, localId)) {
				nodeResult = { node, pos };
			}
			return !nodeResult;
		});

		if (nodeResult) {
			const { node, pos } = nodeResult;
			tr.setNodeMarkup(pos, undefined, getUpdateAttrs(node.attrs, previousMauiId, newMauiId));
			if (fg('platform_remix_fix_node_replacement_selection')) {
				tr.setSelection(NodeSelection.create(tr.doc, pos));
			}
			if (suppressScrollIntoView) {
				tr.setMeta('scrollIntoView', false);
			}
			return tr;
		}

		return null;
	};
